import { useCallback, useEffect, useRef, useState } from "react"
import { api } from "../lib/api"
import type { ApiError } from "../lib/api"
import type { Audio, AudioType } from "../types/scheduled-post"

// リール音源の検索（`GET /instagram_accounts/:id/audios`）。
//
// 検索はユーザーの明示操作（検索ボタン / Enter / タブ切替 / モーダル初期表示）でのみ走らせる。
// debounce 型の type-ahead にしないのは Meta のレート制限（180 コール/時）への影響を抑えるため。
//
// 結果 state はモーダルを閉じても破棄しない。この hook を AudioSection（モーダルより長生き）で
// 呼ぶことで、閉じて開き直したときに再検索が発生しないようにしている。
// ただしこの「破棄しない」は同一アカウント内の話で、アカウントを切り替えたら結果は捨てる（後述）。
export const useAudioSearch = (selectedAccountId: number, organizationId?: number) => {
  const [audioType, setAudioType] = useState<AudioType>("music")
  const [query, setQuery]         = useState("")
  const [results, setResults]     = useState<Audio[]>([])
  const [loading, setLoading]     = useState(false)
  const [error, setError]         = useState<string | null>(null)
  const [searched, setSearched]   = useState(false)

  // React 18 の StrictMode / 再レンダーで初期ロードが二重に走るのを防ぐ。
  // 「成功した結果を保持する」ためのフラグなので、失敗したときは戻して再試行できるようにする
  // （決定 #26 の「2 回目以降は再検索しない」は成功結果の保持が意図なので、これに反しない）
  const initializedRef = useRef(false)

  // リクエスト世代。タブ連打・Enter 連打で並行したコールのうち、
  // 最後に投げたものだけが state を書けるようにする（後着の古い応答で上書きされるのを防ぐ）
  const seqRef = useRef(0)

  const run = useCallback(async (type: AudioType, q: string) => {
    const seq = ++seqRef.current
    setLoading(true)
    setError(null)
    initializedRef.current = true

    // api.get はクエリパラメータ引数を取らないため自前で組み立てる（日本語クエリ対応）
    const params = [`audio_type=${encodeURIComponent(type)}`]
    if (q.trim()) params.push(`q=${encodeURIComponent(q.trim())}`)
    if (organizationId != null) params.push(`organization_id=${organizationId}`)

    try {
      const data = await api.get<{ audios: Audio[] }>(
        `/instagram_accounts/${selectedAccountId}/audios?${params.join("&")}`
      )
      if (seqRef.current !== seq) return
      setResults(data.audios ?? [])
      setSearched(true)
    } catch (e) {
      if (seqRef.current !== seq) return
      const status = (e as ApiError)?.status
      // 失敗した取得は「初期化済み」とみなさない（モーダルを開き直したら再試行できるようにする）
      initializedRef.current = false
      setResults([])
      setError(
        status === 429
          ? "アクセスが集中しています。時間をおいて再試行してください。"
          : (e as ApiError)?.data?.error || "音源の取得に失敗しました"
      )
    } finally {
      // 後続のコールが飛んでいる間はスピナーを消さない
      if (seqRef.current === seq) setLoading(false)
    }
  }, [selectedAccountId, organizationId])

  // アカウントを切り替えたら検索 state を破棄する。
  // 音源の可用性はアカウント単位なので、前のアカウントの検索結果を出したままにすると
  // そのアカウントでは使えない音源を選べてしまい、公開時の failed（＋失敗メール）に至る。
  // 決定 #26「モーダルを閉じても検索結果を破棄しない」は同一アカウント内の話なので、
  // アカウント切替での破棄はこれに反しない。
  const prevAccountIdRef = useRef(selectedAccountId)
  useEffect(() => {
    if (prevAccountIdRef.current === selectedAccountId) return
    prevAccountIdRef.current = selectedAccountId

    // 旧アカウント宛の in-flight 応答が新しい state を書かないよう世代を進める
    seqRef.current++
    initializedRef.current = false
    setQuery("")
    setResults([])
    setError(null)
    setSearched(false)
    setLoading(false)
  }, [selectedAccountId])

  // 検索ボタン / Enter
  const search = useCallback(() => { void run(audioType, query) }, [run, audioType, query])

  // タブ切替（同じ検索語で切替先のタイプを引き直す）
  const selectAudioType = useCallback((type: AudioType) => {
    setAudioType(type)
    void run(type, query)
  }, [run, query])

  // モーダル初期表示。未検索なら 1 コールだけトレンド音源を取る（決定 #4）
  const loadTrendingIfNeeded = useCallback(() => {
    if (initializedRef.current) return
    void run(audioType, "")
  }, [run, audioType])

  return {
    audioType, query, setQuery,
    results, loading, error, searched,
    search, selectAudioType, loadTrendingIfNeeded,
  }
}

export type AudioSearchState = ReturnType<typeof useAudioSearch>
